import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Plus, Trash2, ArrowLeft, Save, FileText, Upload, RefreshCw, Download,
  Image as ImageIcon, X, ListChecks, FileJson,
} from "lucide-react";
import { toast } from "sonner";
import MarkdownEditor from "../../components/MarkdownEditor";

const newQuestion = () => ({
  text: "",
  type: "single",
  options: ["", ""],
  correct: [],
  block: "",
});

const newExam = (order) => ({
  exam_id: null, // null = not yet persisted
  title: "",
  description: "",
  passPercentage: 70,
  timeLimitMinutes: 0,
  order,
  questions: [newQuestion()],
  theoryFiles: [],
});

const fmtSize = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const examToPayload = (ex) => ({
  title: ex.title,
  description: ex.description,
  pass_percentage: Number(ex.passPercentage) || 0,
  time_limit_minutes: Number(ex.timeLimitMinutes) || 0,
  order: Number(ex.order) || 1,
  questions: ex.questions,
});

// ---- Client-side JSON/CSV question parsing (used while the exam doesn't
// exist on the server yet — e.g. exam 1 of a brand-new course — so import
// doesn't need to wait for a save round-trip). Mirrors the shape the
// backend's own import endpoint would produce. ----
const parseCsvRows = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const pushField = () => { row.push(field); field = ""; };
  const pushRow = () => { pushField(); rows.push(row); row = []; };
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += c; i += 1; continue;
    }
    if (c === '"') { inQuotes = true; i += 1; continue; }
    if (c === ",") { pushField(); i += 1; continue; }
    if (c === "\r") { i += 1; continue; }
    if (c === "\n") { pushRow(); i += 1; continue; }
    field += c; i += 1;
  }
  if (field.length > 0 || row.length > 0) pushRow();
  return rows.filter((r) => r.length > 1 || (r.length === 1 && r[0] !== ""));
};

const questionsFromCsvText = (text) => {
  const rows = parseCsvRows(text);
  if (rows.length < 2) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const col = (name) => header.indexOf(name);
  const qi = col("question"), ti = col("type"), oi = col("options"), ci = col("correct"), bi = col("block");
  return rows
    .slice(1)
    .map((r) => {
      const type = ((ti >= 0 ? r[ti] : "") || "single").trim() || "single";
      let options = ((oi >= 0 ? r[oi] : "") || "").split("|").map((o) => o.trim()).filter(Boolean);
      if (type === "truefalse") options = ["True", "False"];
      const correct = ((ci >= 0 ? r[ci] : "") || "")
        .split(",")
        .map((x) => x.trim())
        .filter((x) => /^\d+$/.test(x))
        .map(Number);
      return {
        text: ((qi >= 0 ? r[qi] : "") || "").trim(),
        type,
        options,
        correct,
        block: ((bi >= 0 ? r[bi] : "") || "").trim(),
      };
    })
    .filter((q) => q.text);
};

const questionsFromJsonText = (text) => {
  const data = JSON.parse(text);
  return (data.questions || []).map((q) => ({
    text: q.text || "",
    type: q.type || "single",
    options: q.options || [],
    correct: q.correct || [],
    block: q.block || "",
  }));
};

export default function AdminTestEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState("normal");
  const [studyHours, setStudyHours] = useState("");
  const [price, setPrice] = useState("");
  const [moreInfo, setMoreInfo] = useState("");
  const [area, setArea] = useState("");
  const [exams, setExams] = useState([newExam(1)]);
  const [saving, setSaving] = useState(false);
  const [headerImage, setHeaderImage] = useState(null);
  const [headerBusy, setHeaderBusy] = useState(false);
  const [generalTheoryFile, setGeneralTheoryFile] = useState(null);
  const [generalTheoryBusy, setGeneralTheoryBusy] = useState(false);
  const [examBusy, setExamBusy] = useState(false); // busy state for add/import-exam actions
  const [existingAreas, setExistingAreas] = useState([]);

  useEffect(() => {
    // Fetch unique areas from all existing courses, for the datalist autocomplete
    api.get("/admin/courses").then(({ data }) => {
      const set = new Set();
      (data || []).forEach((c) => {
        if (c.area && c.area.trim()) set.add(c.area.trim());
      });
      setExistingAreas([...set].sort());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/admin/courses/${id}`).then(({ data }) => {
      setTitle(data.title);
      setDescription(data.description || "");
      setKind(data.kind || "normal");
      setStudyHours(data.study_hours ?? "");
      setPrice(data.price ?? "");
      setMoreInfo(data.more_info || "");
      setArea(data.area || "");
      setHeaderImage(data.header_image || null);
      setGeneralTheoryFile(data.general_theory_file || null);
      setExams(
        (data.exams || [])
          .slice()
          .sort((a, b) => (a.order || 1) - (b.order || 1))
          .map((e) => ({
            exam_id: e.exam_id,
            title: e.title || "",
            description: e.description || "",
            passPercentage: e.pass_percentage,
            timeLimitMinutes: e.time_limit_minutes || 0,
            order: e.order || 1,
            questions: (e.questions || []).map((q) => ({
              text: q.text,
              type: q.type,
              options: q.options,
              correct: q.correct || [],
              block: q.block || "",
            })),
            theoryFiles: e.theory_files || [],
          }))
      );
    });
  }, [id]);

  // ---- Question editing helpers (parametrized by exam index) ----
  const updateExam = (examIdx, patch) => {
    setExams((exs) => exs.map((e, i) => (i === examIdx ? { ...e, ...patch } : e)));
  };

  const updateQ = (examIdx, qIdx, patch) => {
    setExams((exs) =>
      exs.map((e, i) =>
        i === examIdx
          ? { ...e, questions: e.questions.map((q, j) => (j === qIdx ? { ...q, ...patch } : q)) }
          : e
      )
    );
  };

  const changeType = (examIdx, qIdx, type) => {
    const q = exams[examIdx].questions[qIdx];
    let options = q.options;
    let correct = [];
    if (type === "truefalse") {
      options = ["Verdadero", "Falso"];
    } else if (q.type === "truefalse") {
      options = ["", ""];
    }
    updateQ(examIdx, qIdx, { type, options, correct });
  };

  const toggleCorrect = (examIdx, qIdx, optIdx, multi) => {
    const q = exams[examIdx].questions[qIdx];
    let next;
    if (multi) {
      next = q.correct.includes(optIdx)
        ? q.correct.filter((x) => x !== optIdx)
        : [...q.correct, optIdx];
    } else {
      next = [optIdx];
    }
    updateQ(examIdx, qIdx, { correct: next });
  };

  const updateOption = (examIdx, qIdx, optIdx, value) => {
    const opts = [...exams[examIdx].questions[qIdx].options];
    opts[optIdx] = value;
    updateQ(examIdx, qIdx, { options: opts });
  };

  const addOption = (examIdx, qIdx) => {
    const opts = [...exams[examIdx].questions[qIdx].options, ""];
    updateQ(examIdx, qIdx, { options: opts });
  };

  const removeOption = (examIdx, qIdx, optIdx) => {
    const q = exams[examIdx].questions[qIdx];
    const opts = q.options.filter((_, i) => i !== optIdx);
    const correct = q.correct.filter((c) => c !== optIdx).map((c) => (c > optIdx ? c - 1 : c));
    updateQ(examIdx, qIdx, { options: opts, correct });
  };

  const addQuestion = (examIdx) => {
    setExams((exs) =>
      exs.map((e, i) => {
        if (i !== examIdx) return e;
        const lastBlock = e.questions.length ? e.questions[e.questions.length - 1].block || "" : "";
        return { ...e, questions: [...e.questions, { ...newQuestion(), block: lastBlock }] };
      })
    );
  };

  const removeQuestion = (examIdx, qIdx) => {
    setExams((exs) =>
      exs.map((e, i) => (i === examIdx ? { ...e, questions: e.questions.filter((_, j) => j !== qIdx) } : e))
    );
  };

  // ---- Exam add/remove ----
  const addExamManually = () => {
    const maxOrder = exams.reduce((m, e) => Math.max(m, Number(e.order) || 0), 0);
    setExams((exs) => [...exs, newExam(maxOrder + 1)]);
  };

  const removeExam = async (examIdx) => {
    const ex = exams[examIdx];
    if (!window.confirm(`¿Eliminar el examen "${ex.title || "sin título"}"?`)) return;
    if (ex.exam_id) {
      try {
        await api.delete(`/admin/courses/${id}/exams/${ex.exam_id}`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar el examen");
        return;
      }
    }
    setExams((exs) => exs.filter((_, i) => i !== examIdx));
    toast.success("Examen eliminado");
  };

  const importNewExamFromFile = async (file) => {
    if (!id) {
      toast.error("Guarda el curso antes de importar un examen");
      return;
    }
    if (!file) return;
    setExamBusy(true);
    try {
      const maxOrder = exams.reduce((m, e) => Math.max(m, Number(e.order) || 0), 0);
      const created = await api.post(`/admin/courses/${id}/exams`, {
        title: file.name.replace(/\.(json|csv)$/i, "") || "Examen importado",
        description: "Examen importado",
        pass_percentage: 70,
        order: maxOrder + 1,
        questions: [],
      });
      const newExamDoc = created.data.exams[created.data.exams.length - 1];
      const fd = new FormData();
      fd.append("file", file);
      const imported = await api.post(
        `/admin/courses/${id}/exams/${newExamDoc.exam_id}/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const serverExam = imported.data.exams.find((e) => e.exam_id === newExamDoc.exam_id);
      setExams((exs) => [
        ...exs,
        {
          exam_id: serverExam.exam_id,
          title: serverExam.title,
          description: serverExam.description,
          passPercentage: serverExam.pass_percentage,
          timeLimitMinutes: serverExam.time_limit_minutes || 0,
          order: serverExam.order,
          questions: serverExam.questions.map((q) => ({
            text: q.text, type: q.type, options: q.options, correct: q.correct || [], block: q.block || "",
          })),
          theoryFiles: serverExam.theory_files || [],
        },
      ]);
      toast.success("Examen importado. Revisa el título/descripción antes de guardar.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar el examen");
    } finally {
      setExamBusy(false);
    }
  };

  const importQuestionsIntoExam = async (examIdx, file) => {
    const ex = exams[examIdx];
    if (!file) return;

    if (!ex.exam_id) {
      // Exam not persisted yet (e.g. exam 1 of a brand-new course) — parse
      // the file locally and populate the form; no server round-trip needed
      // until the whole course is saved.
      const hasContent = ex.questions.some((q) => q.text.trim());
      if (hasContent && !window.confirm("Esto reemplaza las preguntas actuales de este examen. ¿Continuar?")) return;
      try {
        const text = await file.text();
        const fname = file.name.toLowerCase();
        let questions;
        if (fname.endsWith(".json")) {
          questions = questionsFromJsonText(text);
        } else if (fname.endsWith(".csv")) {
          questions = questionsFromCsvText(text);
        } else {
          toast.error("Formato no soportado (usa JSON o CSV)");
          return;
        }
        if (!questions.length) {
          toast.error("No se encontraron preguntas en el archivo");
          return;
        }
        updateExam(examIdx, { questions });
        toast.success("Preguntas importadas. Revísalas y guarda el curso.");
      } catch (err) {
        toast.error(`Archivo inválido: ${err.message}`);
      }
      return;
    }

    if (!window.confirm("Esto reemplaza todas las preguntas actuales de este examen. ¿Continuar?")) return;
    setExamBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/courses/${id}/exams/${ex.exam_id}/import`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const serverExam = data.exams.find((e) => e.exam_id === ex.exam_id);
      updateExam(examIdx, {
        questions: serverExam.questions.map((q) => ({
          text: q.text, type: q.type, options: q.options, correct: q.correct || [], block: q.block || "",
        })),
      });
      toast.success("Preguntas importadas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo importar");
    } finally {
      setExamBusy(false);
    }
  };

  // ---- Validation + save ----
  const validate = () => {
    if (!title.trim()) {
      toast.error("El título del curso es obligatorio");
      return false;
    }
    if (!id && exams.length === 0) {
      toast.error("Añade al menos un examen");
      return false;
    }
    for (let ei = 0; ei < exams.length; ei++) {
      const ex = exams[ei];
      if (!ex.title.trim()) {
        toast.error(`Examen ${ei + 1}: el título es obligatorio`);
        return false;
      }
      if (!ex.description.trim()) {
        toast.error(`Examen ${ei + 1}: la descripción es obligatoria`);
        return false;
      }
      for (let qi = 0; qi < ex.questions.length; qi++) {
        const q = ex.questions[qi];
        if (!q.text.trim()) {
          toast.error(`Examen ${ei + 1}, pregunta ${qi + 1}: falta el texto`);
          return false;
        }
        if (q.options.some((o) => !o.trim())) {
          toast.error(`Examen ${ei + 1}, pregunta ${qi + 1}: opciones vacías`);
          return false;
        }
        if (q.correct.length === 0) {
          toast.error(`Examen ${ei + 1}, pregunta ${qi + 1}: marca al menos una respuesta correcta`);
          return false;
        }
      }
    }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const courseMeta = {
        title,
        description,
        kind,
        study_hours: studyHours === "" ? null : Number(studyHours),
        price: price === "" ? null : Number(price),
        more_info: moreInfo,
        area: area.trim() || null,
      };
      if (!id) {
        await api.post("/admin/courses", { ...courseMeta, exams: exams.map(examToPayload) });
        toast.success("Guardado");
        navigate("/admin/courses");
        return;
      }
      await api.put(`/admin/courses/${id}`, courseMeta);
      const reorderPayload = [];
      for (const ex of exams) {
        if (ex.exam_id) {
          await api.put(`/admin/courses/${id}/exams/${ex.exam_id}`, examToPayload(ex));
          reorderPayload.push({ exam_id: ex.exam_id, order: Number(ex.order) || 1 });
        } else {
          const { data } = await api.post(`/admin/courses/${id}/exams`, examToPayload(ex));
          const created = data.exams[data.exams.length - 1];
          reorderPayload.push({ exam_id: created.exam_id, order: Number(ex.order) || 1 });
        }
      }
      if (reorderPayload.length) {
        await api.post(`/admin/courses/${id}/exams/reorder`, reorderPayload);
      }
      toast.success("Guardado");
      navigate("/admin/courses");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  // ---- Per-exam theory PDFs ----
  const refreshExamTheory = async (examId) => {
    const { data } = await api.get(`/admin/courses/${id}`);
    const serverExam = (data.exams || []).find((e) => e.exam_id === examId);
    if (!serverExam) return;
    setExams((exs) => exs.map((e) => (e.exam_id === examId ? { ...e, theoryFiles: serverExam.theory_files || [] } : e)));
  };

  const uploadExamTheory = async (examIdx, file, replaceFileId = null) => {
    const ex = exams[examIdx];
    if (!ex.exam_id) {
      toast.error("Guarda el curso antes de añadir material a este examen");
      return;
    }
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo PDF");
      return;
    }
    setExamBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = replaceFileId
        ? `/admin/courses/${id}/exams/${ex.exam_id}/theory/${replaceFileId}`
        : `/admin/courses/${id}/exams/${ex.exam_id}/theory`;
      const method = replaceFileId ? "put" : "post";
      await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(replaceFileId ? "PDF actualizado" : "PDF añadido");
      await refreshExamTheory(ex.exam_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir PDF");
    } finally {
      setExamBusy(false);
    }
  };

  const deleteExamTheory = async (examIdx, fileId) => {
    const ex = exams[examIdx];
    if (!window.confirm("¿Eliminar este PDF? Los estudiantes ya no podrán descargarlo.")) return;
    setExamBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/exams/${ex.exam_id}/theory/${fileId}`);
      toast.success("PDF eliminado");
      await refreshExamTheory(ex.exam_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setExamBusy(false);
    }
  };

  const downloadExamTheory = async (examId, fileId, name) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/courses/${id}/exams/${examId}/theory/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "material.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Course-level general PDF ----
  const uploadGeneralTheory = async (file) => {
    if (!id) {
      toast.error("Guarda el curso antes de añadir el PDF general");
      return;
    }
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Solo PDF");
      return;
    }
    setGeneralTheoryBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = generalTheoryFile
        ? `/admin/courses/${id}/general-theory/${generalTheoryFile.file_id}`
        : `/admin/courses/${id}/general-theory`;
      const method = generalTheoryFile ? "put" : "post";
      const { data } = await api[method](url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGeneralTheoryFile(data);
      toast.success("PDF general guardado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir PDF");
    } finally {
      setGeneralTheoryBusy(false);
    }
  };

  const deleteGeneralTheory = async () => {
    if (!generalTheoryFile) return;
    if (!window.confirm("¿Eliminar el PDF general del curso?")) return;
    setGeneralTheoryBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/general-theory/${generalTheoryFile.file_id}`);
      setGeneralTheoryFile(null);
      toast.success("PDF eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setGeneralTheoryBusy(false);
    }
  };

  const downloadGeneralTheory = async () => {
    if (!generalTheoryFile) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/courses/${id}/general-theory/${generalTheoryFile.file_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = generalTheoryFile.original_name || "material.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Header image ----
  const headerPreviewUrl = headerImage
    ? `${API}/public/courses/${id}/header?v=${headerImage.version || headerImage.uploaded_at || ""}`
    : null;

  const uploadHeader = async (file) => {
    if (!id) {
      toast.error("Guarda el curso antes de añadir imagen");
      return;
    }
    if (!file) return;
    const okTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error("Formato no soportado. Usa PNG, JPG o WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Imagen máx. 5 MB");
      return;
    }
    setHeaderBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/courses/${id}/header`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHeaderImage(data);
      toast.success("Imagen guardada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir imagen");
    } finally {
      setHeaderBusy(false);
    }
  };

  const deleteHeader = async () => {
    if (!window.confirm("¿Eliminar la imagen de cabecera?")) return;
    setHeaderBusy(true);
    try {
      await api.delete(`/admin/courses/${id}/header`);
      setHeaderImage(null);
      toast.success("Imagen eliminada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setHeaderBusy(false);
    }
  };

  const sortedExamIndexes = exams
    .map((e, i) => i)
    .sort((a, b) => (Number(exams[a].order) || 1) - (Number(exams[b].order) || 1));

  return (
    <div className="space-y-6" data-testid="test-editor-page">
      <Button variant="ghost" onClick={() => navigate("/admin/courses")} className="text-gray-600">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Volver
      </Button>

      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-gray-900">
          {id ? "Editar curso" : "Nuevo curso"}
        </h1>
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="save-test">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4">
        <div>
          <Label htmlFor="title">Título del curso</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="test-title-input" />
        </div>
        <div>
          <Label htmlFor="desc">Descripción</Label>
          <Textarea id="desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="area">Área formativa</Label>
          <Input
            id="area"
            value={area}
            onChange={(e) => setArea(e.target.value)}
            placeholder="p. ej. Sanidad, Restauración, Servicios Sociales…"
            list="area-suggestions"
            data-testid="test-area-input"
          />
          <datalist id="area-suggestions">
            {(existingAreas || []).map((a) => (
              <option key={a} value={a} />
            ))}
          </datalist>
          <p className="text-xs text-gray-500 mt-1">
            En el catálogo público los cursos se agrupan por área. Deja en blanco para mostrar en &quot;Otros&quot;.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="w-80">
            <Label htmlFor="kind">Tipo de curso</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger id="kind" data-testid="test-kind-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">TALLER — Entrega de certificado</SelectItem>
                <SelectItem value="final">CURSO — Entrega de diploma</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              {kind === "final"
                ? "Al aprobar todos los exámenes, el alumno recibirá un Diploma."
                : "Al aprobar todos los exámenes, el alumno recibirá un Certificado de aprovechamiento."}
            </p>
          </div>
          <div className="w-44">
            <Label htmlFor="sh">Horas lectivas</Label>
            <Input
              id="sh"
              type="number"
              min={0}
              step="0.5"
              value={studyHours}
              onChange={(e) => setStudyHours(e.target.value)}
              placeholder="Ej: 40"
              data-testid="study-hours-input"
            />
            <p className="text-xs text-gray-500 mt-1">Aparecerán en el PDF.</p>
          </div>
          <div className="w-44">
            <Label htmlFor="price">Precio (€)</Label>
            <Input
              id="price"
              type="number"
              min={0}
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Ej: 49,90"
              data-testid="price-input"
            />
            <p className="text-xs text-gray-500 mt-1">Opcional. Vacío = sin precio.</p>
          </div>
        </div>
      </Card>

      {/* "Más info" block (shown in the home modal) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-3" data-testid="more-info-card">
        <div>
          <Label className="text-sm">Más info (catálogo público)</Label>
          <p className="text-xs text-gray-500 mt-1">
            Este texto se muestra en el botón &quot;Más info&quot; del catálogo de la home pública. Admite formato:
            negrita, cursiva, listas, encabezados y enlaces.
          </p>
        </div>
        <MarkdownEditor
          value={moreInfo}
          onChange={setMoreInfo}
          rows={10}
          placeholder="Describe el curso: a quién va dirigido, temario, requisitos, qué incluye…"
          testId="more-info-editor"
        />
      </Card>

      {/* Header image (shown in catalog card + Más info modal) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4" data-testid="header-image-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-brand" />
              <h2 className="font-display text-lg text-gray-900">Imagen de cabecera</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Aparecerá en la tarjeta del catálogo público y en el modal &quot;Más info&quot;. Recomendado:
              proporción 16:9, máx. 5 MB, PNG/JPG/WEBP.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="header-upload"
              type="file"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadHeader(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || headerBusy}
              onClick={() => document.getElementById("header-upload").click()}
              data-testid="upload-header-image"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {headerImage ? "Reemplazar" : "Subir imagen"}
            </Button>
            {headerImage && (
              <Button
                variant="ghost"
                size="sm"
                disabled={headerBusy}
                onClick={deleteHeader}
                title="Eliminar imagen"
                data-testid="delete-header-image"
              >
                <X className="w-3.5 h-3.5 text-red-500" />
              </Button>
            )}
          </div>
        </div>

        {!id ? (
          <p className="text-xs text-gray-500 italic">
            Guarda el curso primero para poder añadir una imagen.
          </p>
        ) : headerImage ? (
          <div
            className="aspect-[16/9] w-full max-w-md rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
            data-testid="header-image-preview"
          >
            <img
              src={headerPreviewUrl}
              alt={title || "Cabecera del curso"}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[16/9] w-full max-w-md rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400 bg-gray-50">
            <div className="text-center px-4">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-xs">Sin imagen. Sube una para destacar el curso en el catálogo.</p>
            </div>
          </div>
        )}
      </Card>

      {/* General theory PDF (course-level, 0..1) */}
      <Card className="p-6 border border-gray-200 shadow-none rounded-lg space-y-4" data-testid="general-theory-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-brand" />
              <h2 className="font-display text-lg text-gray-900">Material general del curso (PDF)</h2>
            </div>
            <p className="text-xs text-gray-500 mt-1 max-w-md">
              Un único PDF opcional visible para el alumno hasta que apruebe TODOS los exámenes del curso.
              Máximo 20 MB.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="general-theory-upload"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadGeneralTheory(f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={!id || generalTheoryBusy}
              onClick={() => document.getElementById("general-theory-upload").click()}
              data-testid="upload-general-theory"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {generalTheoryFile ? "Reemplazar" : "Subir PDF"}
            </Button>
            {generalTheoryFile && (
              <>
                <Button variant="ghost" size="sm" onClick={downloadGeneralTheory} title="Descargar">
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={generalTheoryBusy}
                  onClick={deleteGeneralTheory}
                  title="Eliminar"
                  data-testid="delete-general-theory"
                >
                  <X className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </>
            )}
          </div>
        </div>
        {!id ? (
          <p className="text-xs text-gray-500 italic">Guarda el curso primero para poder añadir el PDF general.</p>
        ) : generalTheoryFile ? (
          <p className="text-sm text-gray-700" data-testid="general-theory-name">
            {generalTheoryFile.original_name} · {fmtSize(generalTheoryFile.size_bytes)}
          </p>
        ) : (
          <p className="text-sm text-gray-500">Sin PDF general.</p>
        )}
      </Card>

      {/* Exámenes */}
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl text-gray-900 flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-brand" /> Exámenes
        </h2>
        <div className="flex gap-2">
          <input
            id="exam-import-input"
            type="file"
            accept=".json,.csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importNewExamFromFile(f);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!id || examBusy}
            onClick={() => document.getElementById("exam-import-input").click()}
            title={!id ? "Guarda el curso primero" : "Crea un examen nuevo importando sus preguntas"}
            data-testid="import-new-exam"
          >
            <FileJson className="w-4 h-4 mr-2" /> Importar examen (JSON/CSV)
          </Button>
          <Button variant="outline" size="sm" onClick={addExamManually} data-testid="add-exam-manual">
            <Plus className="w-4 h-4 mr-2" /> Añadir examen manualmente
          </Button>
        </div>
      </div>

      <div className="text-xs text-gray-500 leading-relaxed bg-white border border-gray-200 rounded-lg p-4" data-testid="exam-import-format-legend">
        <p className="font-medium text-gray-700 mb-1">Formato de importación de un examen</p>
        <p>
          <span className="font-mono-data">JSON</span>:{" "}
          <code className="font-mono-data">{`{ questions: [{ text, type: "single"|"multiple"|"truefalse", options: ["..."], correct: [0,1], block: "opcional" }] }`}</code>
        </p>
        <p className="mt-1">
          <span className="font-mono-data">CSV</span> columnas:{" "}
          <code className="font-mono-data">question, type, options (separadas por |), correct (índices separados por coma), block (opcional)</code>
        </p>
        <p className="mt-1">
          El título, la descripción y el % de aprobado del examen no se importan — rellénalos a mano arriba.
        </p>
      </div>

      <div className="space-y-6">
        {sortedExamIndexes.map((examIdx) => {
          const ex = exams[examIdx];
          return (
            <Card key={examIdx} className="p-6 border-2 border-brand/20 shadow-none rounded-lg space-y-4" data-testid={`exam-card-${examIdx}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-end gap-3 flex-wrap flex-1">
                  <div className="w-28">
                    <Label className="text-xs">Nº de examen</Label>
                    <Input
                      type="number"
                      min={1}
                      value={ex.order}
                      onChange={(e) => updateExam(examIdx, { order: e.target.value })}
                      data-testid={`exam-order-${examIdx}`}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-xs">Título del examen *</Label>
                    <Input
                      value={ex.title}
                      onChange={(e) => updateExam(examIdx, { title: e.target.value })}
                      data-testid={`exam-title-${examIdx}`}
                    />
                  </div>
                  <div className="w-32">
                    <Label className="text-xs">% para aprobar</Label>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={ex.passPercentage}
                      onChange={(e) => updateExam(examIdx, { passPercentage: e.target.value })}
                      data-testid={`exam-pass-percentage-${examIdx}`}
                    />
                  </div>
                  <div className="w-36">
                    <Label className="text-xs">Tiempo límite (min)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={ex.timeLimitMinutes}
                      onChange={(e) => updateExam(examIdx, { timeLimitMinutes: e.target.value })}
                      placeholder="0 = sin límite"
                      data-testid={`exam-time-limit-${examIdx}`}
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeExam(examIdx)}
                  title="Eliminar examen"
                  data-testid={`remove-exam-${examIdx}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
              <div>
                <Label className="text-xs">Descripción del examen *</Label>
                <Textarea
                  value={ex.description}
                  onChange={(e) => updateExam(examIdx, { description: e.target.value })}
                  data-testid={`exam-description-${examIdx}`}
                />
              </div>

              {/* Per-exam theory PDF */}
              <div className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50/50">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs font-medium text-gray-700">Material de este examen (PDF)</span>
                  <div className="flex items-center gap-1">
                    <input
                      id={`exam-theory-new-${examIdx}`}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadExamTheory(examIdx, f);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!ex.exam_id || examBusy}
                      onClick={() => document.getElementById(`exam-theory-new-${examIdx}`).click()}
                      data-testid={`upload-exam-theory-${examIdx}`}
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Añadir PDF
                    </Button>
                    <input
                      id={`exam-questions-import-${examIdx}`}
                      type="file"
                      accept=".json,.csv"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) importQuestionsIntoExam(examIdx, f);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={examBusy}
                      onClick={() => document.getElementById(`exam-questions-import-${examIdx}`).click()}
                      title="Reemplaza las preguntas de este examen (JSON o CSV)"
                      data-testid={`import-questions-${examIdx}`}
                    >
                      <FileJson className="w-3.5 h-3.5 mr-1.5" /> {ex.exam_id ? "Reimportar preguntas" : "Importar preguntas"}
                    </Button>
                  </div>
                </div>
                {!ex.exam_id ? (
                  <p className="text-xs text-gray-500 italic">Guarda el curso primero para gestionar material de este examen.</p>
                ) : ex.theoryFiles.length === 0 ? (
                  <p className="text-xs text-gray-500">Sin PDF para este examen.</p>
                ) : (
                  <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md bg-white">
                    {ex.theoryFiles.map((f) => (
                      <li key={f.file_id} className="flex items-center justify-between gap-3 px-3 py-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-900 truncate">{f.original_name}</span>
                          <span className="text-[10px] text-gray-500 font-mono-data flex-shrink-0">{fmtSize(f.size_bytes)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => downloadExamTheory(ex.exam_id, f.file_id, f.original_name)} title="Descargar">
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <input
                            id={`exam-theory-replace-${examIdx}-${f.file_id}`}
                            type="file"
                            accept="application/pdf,.pdf"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) uploadExamTheory(examIdx, file, f.file_id);
                              e.target.value = "";
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={examBusy}
                            onClick={() => document.getElementById(`exam-theory-replace-${examIdx}-${f.file_id}`).click()}
                            title="Reemplazar"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={examBusy}
                            onClick={() => deleteExamTheory(examIdx, f.file_id)}
                            title="Eliminar"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Questions within this exam */}
              <div className="space-y-3 pt-2">
                {ex.questions.map((q, qIdx) => {
                  const prevBlock = qIdx > 0 ? (ex.questions[qIdx - 1].block || "").trim() : null;
                  const thisBlock = (q.block || "").trim();
                  const showDivider = qIdx === 0 || thisBlock !== prevBlock;
                  return (
                    <div key={qIdx}>
                      {showDivider && (
                        <div className="flex items-center gap-3 mt-4 mb-2">
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#bcdfd9] to-transparent" />
                          <span className="text-[11px] tracking-[0.2em] uppercase text-brand-ink/70 font-mono-data px-2">
                            {thisBlock ? thisBlock : "Sin bloque"}
                          </span>
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#bcdfd9] to-transparent" />
                        </div>
                      )}
                      <Card className="p-4 border border-gray-200 shadow-none rounded-lg space-y-3 bg-gray-50/30">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <span className="text-xs font-medium text-gray-700 pt-2">Pregunta {qIdx + 1}</span>
                          <div className="flex items-end gap-2">
                            <div className="w-48">
                              <Label className="text-xs">Bloque</Label>
                              <Input
                                value={q.block}
                                onChange={(e) => updateQ(examIdx, qIdx, { block: e.target.value })}
                                placeholder="Ej: Conceptos básicos"
                                data-testid={`q-block-${examIdx}-${qIdx}`}
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeQuestion(examIdx, qIdx)}
                              disabled={ex.questions.length === 1}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Texto</Label>
                          <Textarea
                            value={q.text}
                            onChange={(e) => updateQ(examIdx, qIdx, { text: e.target.value })}
                            data-testid={`q-text-${examIdx}-${qIdx}`}
                          />
                        </div>
                        <div className="w-56">
                          <Label className="text-xs">Tipo</Label>
                          <Select value={q.type} onValueChange={(v) => changeType(examIdx, qIdx, v)}>
                            <SelectTrigger data-testid={`q-type-${examIdx}-${qIdx}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="single">Opción única</SelectItem>
                              <SelectItem value="multiple">Opciones múltiples</SelectItem>
                              <SelectItem value="truefalse">Verdadero / Falso</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Opciones (marca la/las correctas)</Label>
                          {q.type === "single" ? (
                            <RadioGroup
                              value={q.correct[0]?.toString() ?? ""}
                              onValueChange={(v) => toggleCorrect(examIdx, qIdx, Number(v), false)}
                            >
                              {q.options.map((opt, oi) => (
                                <div key={oi} className="flex items-center gap-2">
                                  <RadioGroupItem value={oi.toString()} id={`r-${examIdx}-${qIdx}-${oi}`} />
                                  <Input
                                    value={opt}
                                    onChange={(e) => updateOption(examIdx, qIdx, oi, e.target.value)}
                                    className="flex-1"
                                  />
                                  {q.type !== "truefalse" && q.options.length > 2 && (
                                    <Button variant="ghost" size="sm" onClick={() => removeOption(examIdx, qIdx, oi)}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                            </RadioGroup>
                          ) : (
                            q.options.map((opt, oi) => (
                              <div key={oi} className="flex items-center gap-2">
                                <Checkbox
                                  checked={q.correct.includes(oi)}
                                  onCheckedChange={() => toggleCorrect(examIdx, qIdx, oi, true)}
                                />
                                <Input
                                  value={opt}
                                  onChange={(e) => updateOption(examIdx, qIdx, oi, e.target.value)}
                                  className="flex-1"
                                  disabled={q.type === "truefalse"}
                                />
                                {q.type !== "truefalse" && q.options.length > 2 && (
                                  <Button variant="ghost" size="sm" onClick={() => removeOption(examIdx, qIdx, oi)}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                            ))
                          )}
                          {q.type !== "truefalse" && (
                            <Button variant="outline" size="sm" onClick={() => addOption(examIdx, qIdx)}>
                              <Plus className="w-3.5 h-3.5 mr-1" /> Añadir opción
                            </Button>
                          )}
                        </div>
                      </Card>
                    </div>
                  );
                })}
                <Button variant="outline" size="sm" onClick={() => addQuestion(examIdx)} data-testid={`add-question-${examIdx}`}>
                  <Plus className="w-4 h-4 mr-2" /> Añadir pregunta
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
